import { observer } from 'mobx-react';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_SEARCH_CATEGORY_FIELDS
} from '../../../../utils/constants/fields';
import MultiSelect from '../../../../components/inputs/MultiSelect';
import { useStore } from '../../../../store/store';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES } from '../../../../utils/constants/searchCategories';

const ProfilerIndustryCodesSearchFormMobile = observer(
  ({
    searchTypeInput,
    onShowSection = null,
    fields,
    fieldsCount,
    isSearchDisabled,
    onSearch,
    erroredSections = {},
    categoryFields,
    setCategoryFieldValue,
    categoryOptions,
    searchTreeToggled,
    toggleSearchTree,
    isTyping,
    searchKey,
    setSearchKey,
    industryCodesFlat,
    industryCodesRaw,
    toggleIndustryCode,
    selectIndustryCode,
    toggleAll,
    untoggleAll,
    clearAll
  }) => {
    const { utilsStore, profilerAccessStore } = useStore();

    const searchTreeToggledRef = useRef(searchTreeToggled);

    const onSetSection = (section) => {
      if (typeof onShowSection === 'function') {
        onShowSection(section);
      }
    };

    useEffect(() => {
      searchTreeToggledRef.current = searchTreeToggled;
    }, [searchTreeToggled]);

    useEffect(() => {
      return () => {
        if (searchTreeToggledRef.current) {
          toggleSearchTree();
        }
      };
    }, []);

    const selectedIndustryCodes = industryCodesFlat.filter((code) => code.selected);

    // Memoize highlightMatches function to avoid unnecessary recomputations
    const highlightMatches = useCallback((name, searchKey) => {
      if (!searchKey.trim()) {
        return name;
      }

      const lowerName = name.toLowerCase();
      const lowerSearch = searchKey.toLowerCase();
      const result = [];
      let lastIndex = 0;
      let index = lowerName.indexOf(lowerSearch);

      while (index !== -1) {
        if (index > lastIndex) {
          result.push(name.substring(lastIndex, index));
        }

        result.push(
          <span key={index} className="highlight">
            {name.substring(index, index + searchKey.length)}
          </span>
        );

        lastIndex = index + searchKey.length;
        index = lowerName.indexOf(lowerSearch, lastIndex);
      }

      if (lastIndex < name.length) {
        result.push(name.substring(lastIndex));
      }

      return result;
    }, []);

    const industryCodesMap = useMemo(() => {
      const map = new Map();
      industryCodesFlat.forEach((code) => map.set(code.value, code));
      return map;
    }, [industryCodesFlat]);

    // Memoize hasSelectedChildren function
    const hasSelectedChildren = useCallback(
      (node) => {
        const currentNode = industryCodesMap.get(node.value);
        if (!currentNode) return false;

        const directSelected = currentNode.children?.some((child) => {
          const childNode = industryCodesMap.get(child.value);
          return childNode?.selected;
        });
        if (directSelected) return true;

        return (
          currentNode.children?.some((child) => {
            const childNode = industryCodesMap.get(child.value);
            return childNode ? hasSelectedChildren(childNode) : false;
          }) || false
        );
      },
      [industryCodesMap]
    );

    // Memoize renderIndustryCode function to avoid unnecessary renders
    const renderIndustryCode = useCallback(
      (code, level) => {
        const fromFlat = industryCodesFlat.find((_c) => _c.value === code.value);
        const hasParent = !!fromFlat.parent;
        const hasChildren = !!code.children?.length;
        const isToggled = !!fromFlat.toggled;
        const isSelected = !!fromFlat.selected;
        const showInnerSelectionsDot =
          !isToggled &&
          !!fromFlat.children?.length &&
          hasSelectedChildren(fromFlat, industryCodesFlat);

        return (
          <div
            className="industry-code-item-wrap"
            key={code.value}
            style={hasParent ? { marginLeft: '22px' } : {}}>
            <div
              className={`industry-code-item noselect ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                if (hasChildren) {
                  toggleIndustryCode(code);
                } else {
                  selectIndustryCode(code);
                }
              }}>
              {hasChildren && (
                <IconButton
                  icon={
                    isToggled ? ICON_BUTTON_ICONS.CHEVRON_DOWN : ICON_BUTTON_ICONS.CHEVRON_RIGHT
                  }
                  withFill={false}
                  withBorder={false}
                  wrapClassName="industry-code-item-toggle"
                />
              )}
              {!hasChildren && <div style={{ width: '18px', height: '18px', flexShrink: 0 }} />}
              <IconButton
                icon={
                  isSelected
                    ? ICON_BUTTON_ICONS.CHECK_SQUARE_FILL
                    : showInnerSelectionsDot
                      ? ICON_BUTTON_ICONS.CHECK_SQUARE
                      : ICON_BUTTON_ICONS.SQUARE
                }
                type={ICON_BUTTON_TYPES.BLUE}
                withFill={false}
                withBorder={false}
                wrapClassName={`industry-code-item-check ${!isSelected && showInnerSelectionsDot ? 'light-blue' : ''}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.nativeEvent?.preventDefault();
                  selectIndustryCode(code);
                }}
              />
              <div className="industry-code-item-name">
                {isTyping ? code.name : highlightMatches(code.name, searchKey)}
              </div>
              {showInnerSelectionsDot && <div className="inner-selection-dot" />}
            </div>
            {hasChildren &&
              isToggled &&
              code.children.map((child) => renderIndustryCode(child, level + 1))}
          </div>
        );
      },
      [
        highlightMatches,
        hasSelectedChildren,
        industryCodesFlat,
        selectIndustryCode,
        toggleIndustryCode,
        isTyping,
        searchKey
      ]
    );

    const disableSearchForm =
      profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit;

    return (
      <div className="formform mobile">
        {profilerAccessStore.isProfilerDemo && (
          <div
            className={`profiler-demo-info-message${
              profilerAccessStore.demoSearchExceededLimit ? ' exceeded' : ''
            }`}>
            <div className="img-wrap">
              <img src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE} />
            </div>
            <div className="text">
              {profilerAccessStore.demoSearchExceededLimit ? (
                <>
                  You&apos;ve used all {profilerAccessStore.demoSearchMaxCount} demo searches -
                  upgrade to full access to continue exploring.
                </>
              ) : (
                <>
                  You have {profilerAccessStore.demoSearchRemainingCount} out of{' '}
                  {profilerAccessStore.demoSearchMaxCount} demo searches remaining - upgrade to full
                  access to unlock unlimited searches.
                </>
              )}
            </div>
          </div>
        )}
        <div className="mobile-search-form-title">Select search type of preference</div>
        {searchTypeInput}
        <MultiSelect
          field={PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
          value={categoryFields[PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME]}
          setFieldValue={setCategoryFieldValue}
          values={categoryOptions}
        />

        <div className="search-key-input-wrapper-industry-codes">
          <div className="search-key-label-industry-codes">
            Industries<font className="red-asterisk">*</font>
          </div>
          <div className="search-key-input-wrap-industry-codes">
            <input
              className={`${selectedIndustryCodes.length ? 'placeholder-black' : ''} ${searchTreeToggled ? 'force-focused' : ''}`}
              type="text"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              placeholder={
                selectedIndustryCodes.length === 1
                  ? selectedIndustryCodes[0].name
                  : selectedIndustryCodes.length > 1
                    ? `${selectedIndustryCodes.length} selected`
                    : 'Select'
              }
              disabled={disableSearchForm}
              onFocus={() => {
                if (!searchTreeToggled) {
                  toggleSearchTree();
                }
              }}
            />
            <IconButton
              id="profiler-industry-codes-drop-icon"
              icon={
                searchTreeToggled ? ICON_BUTTON_ICONS.CHEVRON_UP : ICON_BUTTON_ICONS.CHEVRON_DOWN
              }
              withFill={false}
              withBorder={false}
              onClick={toggleSearchTree}
              disabled={disableSearchForm}
            />
            {searchTreeToggled && (
              <div className="industry-codes-wrapper-outer">
                <div className="industry-codes-actions-wrapper">
                  <IconButton
                    type={ICON_BUTTON_TYPES.BLUE}
                    withFill={false}
                    withBorder={false}
                    innerText="Toggle All"
                    onClick={toggleAll}
                  />
                  <IconButton
                    type={ICON_BUTTON_TYPES.BLUE}
                    withFill={false}
                    withBorder={false}
                    innerText="Untoggle All"
                    onClick={untoggleAll}
                  />
                  <IconButton
                    type={ICON_BUTTON_TYPES.BLUE}
                    withFill={false}
                    withBorder={false}
                    innerText="Clear All"
                    onClick={clearAll}
                  />
                </div>
                <div className="industry-codes-wrapper">
                  {industryCodesRaw.map((code) => renderIndustryCode(code, 1))}
                </div>
                <div className="fade-top" />
                <div className="fade-bottom" />
              </div>
            )}
          </div>
        </div>

        {
          fields[
            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.ID +
              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MAX.ID
          ]
        }

        {Object.values(PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES).map((section) => {
          const { filled, total } = fieldsCount[section];
          const btnClassNames = ['btn', 'btn-next'];
          if (erroredSections[section]) {
            btnClassNames.push('error');
          }
          if (
            (profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit) ||
            !profilerAccessStore.canUseProfilerSearch
          ) {
            btnClassNames.push('disabled');
          }
          return (
            <button
              className={btnClassNames.join(' ')}
              key={section}
              onClick={() => {
                if (
                  (profilerAccessStore.isProfilerDemo &&
                    profilerAccessStore.demoSearchExceededLimit) ||
                  !profilerAccessStore.canUseProfilerSearch
                ) {
                  return;
                }
                onSetSection(section);
                window.scrollTo({ top: 0 });
              }}>
              {section} {filled}/{total}
            </button>
          );
        })}

        <div className="mobile-fields-mandatory-row" style={{ padding: 0 }}>
          <font className="red-asterisk">*</font>mandatory fields
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSearchDisabled}
          onClick={onSearch}>
          Search
        </button>
      </div>
    );
  }
);

export default ProfilerIndustryCodesSearchFormMobile;
